import type { GridSnapshot, SimEvent } from "@reagent/sim";

/**
 * The induction, as a list of things to notice and do.
 *
 * A step advances on evidence rather than on a "next" button wherever it can:
 * the tutorial should be watching the player, not asking them to confirm that
 * they read it. Only the opening and closing panels — which ask for no action
 * — need a click.
 *
 * Two kinds of evidence, because two kinds of thing are worth waiting for:
 *
 *  - `whenSnapshot` reads state. Right for "the pipe now exists", which is
 *    true forever after and stays true if the player reloads mid-induction.
 *  - `whenEvent` reads the event stream. Right for things that leave no trace
 *    in the snapshot — playing a Cooling Jacket changes a stat the protocol
 *    does not expose, so the only honest way to know is to watch it happen.
 */
export interface TutorialStep {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  /** Advances as soon as this reads true of the board. */
  readonly whenSnapshot?: (snapshot: GridSnapshot) => boolean;
  /** Advances when a matching event goes past. */
  readonly whenEvent?: (event: SimEvent) => boolean;
  /** Label for the button that advances a step with no evidence to wait for. */
  readonly button?: string;
}

const isJacket = (cardId: string) => cardId.startsWith("card-cooling-jacket");

export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    id: "bench",
    title: "Your bench",
    body:
      "Two feed tanks on the left, a collection hopper on the right, and a mixer in the middle — " +
      "that is every stage in this game, in miniature. Acid and base arrive as packets, one unit " +
      "at a time, and the mixer turns them into salt and water.",
    button: "go on",
  },
  {
    id: "jacket",
    title: "Play a card onto a machine",
    body:
      "Drag the Cooling Jacket from your hand onto the mixer. Point at it first and the Inspector " +
      "on the left reads its heat against the limit it cannot cross — a jacket sheds exactly as " +
      "much heat as the reaction makes, so once it is fitted that counter stops climbing. You " +
      "will not strictly need it here. You will next time.",
    whenEvent: (event) => event.type === "card.played" && isJacket(event.cardId),
  },
  {
    id: "pipe",
    title: "Connect the mixer to the hopper",
    body:
      "The line stops at the mixer — there is nowhere for the product to go. Pipes take two " +
      "clicks because they join two machines: pick up the Glass Pipe, click the mixer, then " +
      "click the hopper.",
    whenSnapshot: (snapshot) =>
      snapshot.edges.some((edge) => edge.fromNodeId === "mix" && edge.toNodeId === "out"),
  },
  {
    id: "run",
    title: "Throw the switch",
    body:
      "Everything so far was planning. The big lever on the right ends the Play phase and starts " +
      "the clock — from then until the turn is spent, the rig runs on its own and you only watch.",
    whenSnapshot: (snapshot) => snapshot.phase === "run",
  },
  {
    id: "watch",
    title: "Watch the line",
    body:
      "Each shape is a reagent: the amber cubes are base, the magenta diamonds acid, and the " +
      "mint crosses coming out of the mixer are your product. Shape, not just colour — you are " +
      "meant to read a running line at a glance.",
    whenSnapshot: (snapshot) => (snapshot.objective.collected.NaCl ?? 0) > 0,
  },
  {
    id: "quota",
    title: "Make quota",
    body:
      "Three units in the hopper and the batch is yours. If the turn runs out first you get a " +
      "fresh hand and another one — you have eight here, and will have far fewer later.",
    whenSnapshot: (snapshot) => snapshot.isRunComplete,
  },
  {
    id: "done",
    title: "That is the whole game",
    body:
      "A shift is five batches like that one, played with a single deck that grows and gets cut " +
      "between them. There is no retrying a batch: fail one and the shift is over. Ready?",
    button: "clock on",
  },
];
